import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import {
  BOTTOM_NAV_EDGE_SX,
  BOTTOM_NAV_FACTORY_ORDER,
  BOTTOM_NAV_ICONS_PER_ROW,
  BOTTOM_NAV_HANDLE_PX,
  BOTTOM_NAV_ROW_PX,
  clampIconsPerRow,
  computeBottomNavHeightPx,
  normalizeBottomNavOrder,
  publishBottomNavHeightPx,
  readBottomNavPrefsCache,
  slotDndId,
  writeBottomNavPrefsCache,
  type BottomNavItemId,
  type BottomNavLayoutMode,
  type BottomNavRowsMode,
  type BottomNavSlot,
} from '../../constants/bottomNav';
import {
  getBottomNavPreferences,
  updateBottomNavPreferences,
} from '../../services/api';
import { canViewCameras, canViewMusic, canViewAc, canViewHeating, canUsePhone, canAnswerDoorbell, getUser, type User } from '../../services/auth';
import { eventBus } from '../../services/EventBus';
import BottomNavIcon from './BottomNavIcon';

export type BottomNavScreen =
  | 'dashboard'
  | 'devices'
  | 'settings'
  | 'realtime'
  | 'climate'
  | 'widgets'
  | 'reports'
  | 'favorites'
  | 'video'
  | 'assistant'
  | 'automations'
  | 'alarm'
  | 'music'
  | 'softphone';

export interface BottomNavBarProps {
  currentScreen: BottomNavScreen | string;
  isHomeActive: boolean;
  isInfoActive: boolean;
  isSettingsActive: boolean;
  isHorizontal: boolean;
  canViewSettings: boolean;
  videoModuleAvailable: boolean;
  user: User | null;
  websocketStatus: 'connected' | 'disconnected' | 'connecting';
  onNavigate: (id: BottomNavItemId) => void;
  /** Info abre overlay; no debe pasar por onNavigate. */
  onInfoClick: () => void;
  onHeightChange?: (heightPx: number) => void;
}

interface SortableNavSlotProps {
  index: number;
  slot: BottomNavSlot;
  active: boolean;
  editing: boolean;
  tooltip: string;
  onClick: () => void;
}

const SortableNavSlot: React.FC<SortableNavSlotProps> = ({
  index,
  slot,
  active,
  editing,
  tooltip,
  onClick,
}) => {
  const dndId = slotDndId(index, slot);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: dndId,
    disabled: !editing,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
    zIndex: isDragging ? 2 : 1,
    touchAction: editing ? 'none' : 'manipulation',
  };

  if (!slot) {
    return (
      <Box
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...(editing ? listeners : {})}
        sx={{
          width: 'auto',
          minWidth: 28,
          maxWidth: 40,
          flex: '1 1 0',
          height: BOTTOM_NAV_ROW_PX - 4,
          flexShrink: 1,
          borderRadius: 1,
          border: editing ? '1px dashed #4caf50' : '1px solid transparent',
        }}
        aria-label={`empty-${index}`}
      />
    );
  }

  return (
    <Tooltip title={tooltip} arrow disableHoverListener={editing}>
      <IconButton
        ref={setNodeRef}
        style={style}
        {...attributes}
        {...(editing ? listeners : {})}
        onClick={(e) => {
          if (editing) {
            e.preventDefault();
            return;
          }
          onClick();
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingY: 0.5,
          flex: '1 1 0',
          width: 'auto',
          minWidth: 28,
          maxWidth: 40,
          height: BOTTOM_NAV_ROW_PX - 4,
          borderRadius: 0,
          color: 'transparent',
          '&:hover': { backgroundColor: 'transparent' },
        }}
      >
        <BottomNavIcon id={slot} active={active} editing={editing} />
      </IconButton>
    </Tooltip>
  );
};

function isItemActive(
  id: BottomNavItemId,
  currentScreen: string,
  isHomeActive: boolean,
  isInfoActive: boolean,
  _isSettingsActive: boolean,
): boolean {
  switch (id) {
    case 'home':
      return isHomeActive || currentScreen === 'dashboard' || currentScreen === 'devices';
    case 'info':
      return isInfoActive;
    case 'assistant':
      return currentScreen === 'assistant' || currentScreen === 'automations';
    case 'phone':
      return currentScreen === 'softphone' || currentScreen === 'phone';
    case 'help':
      return false;
    default:
      return currentScreen === id;
  }
}

function isSlotAllowed(
  id: BottomNavItemId,
  opts: {
    videoModuleAvailable: boolean;
    user: User | null;
    canViewSettings: boolean;
    isHorizontal: boolean;
    hideHelp?: boolean;
  },
): boolean {
  if (id === 'video' && !(opts.videoModuleAvailable && canViewCameras(opts.user))) return false;
  if (id === 'music' && !canViewMusic(opts.user)) return false;
  if (id === 'phone' && !canUsePhone(opts.user) && !canAnswerDoorbell(opts.user)) return false;
  if (id === 'climate' && !(canViewHeating(opts.user) || canViewAc(opts.user))) return false;
  if (id === 'info' && opts.isHorizontal) return false;
  if (id === 'help' && opts.hideHelp) return false;
  return true;
}

const BottomNavBar: React.FC<BottomNavBarProps> = ({
  currentScreen,
  isHomeActive,
  isInfoActive,
  isSettingsActive,
  isHorizontal,
  canViewSettings,
  videoModuleAvailable,
  user,
  websocketStatus,
  onNavigate,
  onInfoClick,
  onHeightChange,
}) => {
  const { t } = useTranslation();
  const userId = user?.id ?? getUser()?.id;
  const [bootCache] = useState(() =>
    readBottomNavPrefsCache(user?.id ?? getUser()?.id),
  );

  const [order, setOrder] = useState<BottomNavSlot[]>(() => {
    if (!bootCache?.order) return BOTTOM_NAV_FACTORY_ORDER;
    return normalizeBottomNavOrder(bootCache.order, {
      layoutMode: bootCache.layout_mode ?? 'single_row',
      iconsPerRow: bootCache.icons_per_row ?? BOTTOM_NAV_ICONS_PER_ROW,
    });
  });
  const [layoutMode, setLayoutMode] = useState<BottomNavLayoutMode>(
    () => bootCache?.layout_mode ?? 'single_row',
  );
  const [iconsPerRow, setIconsPerRow] = useState(
    () => bootCache?.icons_per_row ?? BOTTOM_NAV_ICONS_PER_ROW,
  );
  const [userReorderAllowed, setUserReorderAllowed] = useState(
    () => bootCache?.user_reorder_allowed ?? true,
  );
  const [rowsMode, setRowsMode] = useState<BottomNavRowsMode>(
    () => bootCache?.rows_mode ?? 'collapse',
  );
  const [rowsModeLocked, setRowsModeLocked] = useState(
    () => bootCache?.rows_mode_locked ?? false,
  );
  /** Sin caché: no pintar chrome multi (handle) hasta tener respuesta del server. */
  const [prefsReady, setPrefsReady] = useState(() => Boolean(bootCache));
  const [expanded, setExpanded] = useState(false);
  const [reordering] = useState(false);
  const [dragY0, setDragY0] = useState<number | null>(null);

  const isSingleRowAdmin = layoutMode === 'single_row';
  // Landscape: siempre una fila con todos. Vertical single_row: igual.
  // Mientras no hay prefs: forzar visual de una fila para no flashar handle/alto extra.
  const forceSingleVisual = isHorizontal || isSingleRowAdmin || !prefsReady;

  const loadPrefs = useCallback(async (isCancelled?: () => boolean) => {
    if (!userId) {
      setPrefsReady(true);
      return;
    }
    try {
      const data = await getBottomNavPreferences(userId);
      if (isCancelled?.()) return;
      const mode: BottomNavLayoutMode =
        data.layout_mode === 'multi_row' ? 'multi_row' : 'single_row';
      const ipr = clampIconsPerRow(
        data.icons_per_row ?? BOTTOM_NAV_ICONS_PER_ROW,
        data.max_icons_per_row,
      );
      const normalized = normalizeBottomNavOrder(data.order, {
        layoutMode: mode,
        iconsPerRow: ipr,
      });
      const locked =
        mode === 'single_row' ||
        Boolean(data.rows_mode_locked) ||
        data.rows_policy === 'force_two';
      const nextRowsMode: BottomNavRowsMode =
        mode === 'single_row'
          ? 'collapse'
          : locked || data.rows_mode === 'always_two'
            ? 'always_two'
            : 'collapse';
      const reorderAllowed = Boolean(data.user_reorder_allowed) && mode === 'multi_row';
      setLayoutMode(mode);
      setIconsPerRow(ipr);
      setUserReorderAllowed(reorderAllowed);
      setOrder(normalized);
      setRowsModeLocked(locked);
      setRowsMode(nextRowsMode);
      writeBottomNavPrefsCache({
        userId,
        layout_mode: mode,
        icons_per_row: ipr,
        order: normalized,
        rows_mode: nextRowsMode,
        rows_mode_locked: locked,
        user_reorder_allowed: reorderAllowed,
      });
    } catch {
      if (isCancelled?.()) return;
      setOrder(BOTTOM_NAV_FACTORY_ORDER);
      setLayoutMode('single_row');
      setRowsMode('collapse');
      setRowsModeLocked(false);
      setUserReorderAllowed(true);
    } finally {
      if (!isCancelled?.()) setPrefsReady(true);
    }
  }, [userId]);

  useEffect(() => {
    let cancelled = false;
    void loadPrefs(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [loadPrefs]);

  // Settings (orden default admin) actualiza API/caché pero la barra ya está montada
  useEffect(() => {
    return eventBus.on('bottom-nav:prefs-updated', () => {
      void loadPrefs();
    });
  }, [loadPrefs]);

  /** Ranuras visibles; sin permiso → vacío (multi) o se omite (single/landscape pack). */
  const layoutSlots = useMemo(() => {
    const allowOpts = {
      videoModuleAvailable,
      user,
      canViewSettings,
      isHorizontal,
      hideHelp: true,
    };
    const mapped = order.map((slot) => {
      if (!slot) return null;
      return isSlotAllowed(slot, allowOpts) ? slot : null;
    });
    if (forceSingleVisual) {
      // Compacto, sin huecos (landscape o admin single_row)
      return mapped.filter((s): s is BottomNavItemId => s != null);
    }
    return mapped;
  }, [
    order,
    videoModuleAvailable,
    user,
    canViewSettings,
    isHorizontal,
    forceSingleVisual,
  ]);

  const totalSlots = forceSingleVisual ? layoutSlots.length : iconsPerRow * 2;
  const hasRow2Content = useMemo(
    () =>
      !forceSingleVisual &&
      layoutSlots.slice(iconsPerRow, totalSlots).some((s) => s != null),
    [layoutSlots, forceSingleVisual, iconsPerRow, totalSlots],
  );

  const canUseTwoRows =
    !forceSingleVisual && (hasRow2Content || rowsMode === 'always_two' || reordering);
  const effectiveExpanded =
    canUseTwoRows && (rowsMode === 'always_two' || expanded || reordering);
  const showHandle =
    !forceSingleVisual && rowsMode === 'collapse' && !reordering && hasRow2Content;
  const showSecondRow = !forceSingleVisual && (effectiveExpanded || reordering);

  const row1 = forceSingleVisual
    ? layoutSlots
    : layoutSlots.slice(0, iconsPerRow);
  const row2 = showSecondRow ? layoutSlots.slice(iconsPerRow, totalSlots) : [];

  const heightPx = computeBottomNavHeightPx({
    needsTwoRows: showSecondRow,
    expanded: true,
    showHandle,
    singleRow: forceSingleVisual,
  });

  useEffect(() => {
    onHeightChange?.(heightPx);
  }, [heightPx, onHeightChange]);

  useEffect(() => {
    publishBottomNavHeightPx(heightPx);
    return () => publishBottomNavHeightPx(null);
  }, [heightPx]);

  useEffect(() => {
    if (!canUseTwoRows) setExpanded(false);
  }, [canUseTwoRows]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 80, tolerance: 10 } }),
  );

  const dndSlots =
    reordering || forceSingleVisual || showSecondRow
      ? layoutSlots
      : layoutSlots.slice(0, iconsPerRow);
  const dndIds = useMemo(
    () => dndSlots.map((slot, i) => slotDndId(i, slot)),
    [dndSlots],
  );

  const persistOrder = useCallback(
    async (next: BottomNavSlot[]) => {
      if (!userReorderAllowed) return;
      const normalized = normalizeBottomNavOrder(next, {
        layoutMode: 'multi_row',
        iconsPerRow,
      });
      setOrder(normalized);
      if (!userId) return;
      writeBottomNavPrefsCache({
        userId,
        layout_mode: 'multi_row',
        icons_per_row: iconsPerRow,
        order: normalized,
        rows_mode: rowsMode,
        rows_mode_locked: rowsModeLocked,
        user_reorder_allowed: userReorderAllowed,
      });
      try {
        await updateBottomNavPreferences(userId, { order: normalized });
      } catch {
        /* ignore */
      }
    },
    [userId, userReorderAllowed, iconsPerRow, rowsMode, rowsModeLocked],
  );

  const handleItemClick = useCallback(
    (id: BottomNavItemId) => {
      if (reordering || id === 'help') return;
      if (id === 'info') {
        onInfoClick();
        return;
      }
      onNavigate(id);
      if (!forceSingleVisual && rowsMode === 'collapse' && canUseTwoRows) {
        setExpanded(false);
      }
    },
    [
      reordering,
      onNavigate,
      onInfoClick,
      rowsMode,
      canUseTwoRows,
      forceSingleVisual,
    ],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!userReorderAllowed || reordering === false) {
        // allow drag only in reorder mode
      }
      if (!userReorderAllowed || !reordering) return;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const working = [...layoutSlots];
      const oldIndex = dndIds.indexOf(String(active.id));
      const newIndex = dndIds.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;

      const base =
        dndSlots.length === totalSlots
          ? arrayMove(working, oldIndex, newIndex)
          : (() => {
              const row = arrayMove(working.slice(0, iconsPerRow), oldIndex, newIndex);
              return [...row, ...working.slice(iconsPerRow)];
            })();

      const allowOpts = {
        videoModuleAvailable,
        user,
        canViewSettings,
        isHorizontal,
        hideHelp: true,
      };
      const result = [...base];
      while (result.length < totalSlots) result.push(null);
      const present = new Set(result.filter(Boolean) as BottomNavItemId[]);
      for (const orig of order) {
        if (orig && !isSlotAllowed(orig, allowOpts) && !present.has(orig)) {
          const free = result.findIndex((s) => s === null);
          if (free >= 0) {
            result[free] = orig;
            present.add(orig);
          }
        }
      }
      void persistOrder(result.slice(0, totalSlots));
    },
    [
      userReorderAllowed,
      reordering,
      layoutSlots,
      dndIds,
      dndSlots.length,
      totalSlots,
      iconsPerRow,
      order,
      videoModuleAvailable,
      user,
      canViewSettings,
      isHorizontal,
      persistOrder,
    ],
  );

  const onHandlePointerDown = (clientY: number) => setDragY0(clientY);
  const onHandlePointerMove = (clientY: number) => {
    if (dragY0 == null) return;
    const delta = dragY0 - clientY;
    if (delta > 24) setExpanded(true);
    else if (delta < -24) setExpanded(false);
  };
  const onHandlePointerUp = () => setDragY0(null);

  const renderRow = (slots: BottomNavSlot[], indexOffset: number) => (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        height: BOTTOM_NAV_ROW_PX,
        width: '100%',
        boxSizing: 'border-box',
        pl: BOTTOM_NAV_EDGE_SX,
        pr: BOTTOM_NAV_EDGE_SX,
        overflow: 'hidden',
      }}
    >
      {slots.map((slot, i) => {
        const index = indexOffset + i;
        return (
          <SortableNavSlot
            key={slotDndId(index, slot)}
            index={index}
            slot={slot}
            active={
              !!slot &&
              isItemActive(slot, currentScreen, isHomeActive, isInfoActive, isSettingsActive)
            }
            editing={reordering && userReorderAllowed}
            tooltip={slot ? (t(`bottomNav.tooltips.${slot}`) as string) : ''}
            onClick={() => {
              if (slot) handleItemClick(slot);
            }}
          />
        );
      })}
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1400,
          backgroundColor: '#333',
          borderTop: '1px solid #555',
          height: heightPx,
          display: 'flex',
          flexDirection: 'column',
          userSelect: reordering ? 'none' : 'auto',
        }}
      >
        {showHandle && (
          <Box
            onClick={() => setExpanded((v) => !v)}
            onPointerDown={(e) => {
              (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
              onHandlePointerDown(e.clientY);
            }}
            onPointerMove={(e) => onHandlePointerMove(e.clientY)}
            onPointerUp={onHandlePointerUp}
            onPointerCancel={onHandlePointerUp}
            sx={{
              height: BOTTOM_NAV_HANDLE_PX,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'grab',
              touchAction: 'none',
            }}
            aria-label={t('bottomNav.handleAria') as string}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                borderRadius: 2,
                backgroundColor: '#888',
              }}
            />
          </Box>
        )}

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={dndIds} strategy={rectSortingStrategy}>
            {renderRow(row1, 0)}
            {row2.length > 0 && renderRow(row2, iconsPerRow)}
          </SortableContext>
        </DndContext>

        <Box
          sx={{
            position: 'absolute',
            // single_row (admin): casi al borde inferior de la barra.
            // multi_row: sobre la franja del handle (o cerca del borde superior si no hay handle).
            ...(isSingleRowAdmin
              ? { top: 'auto', bottom: 6, transform: 'none' }
              : {
                  top: showHandle ? BOTTOM_NAV_HANDLE_PX / 2 : 6,
                  bottom: 'auto',
                  transform: 'translateY(-50%)',
                }),
            right: 8,
            width: 4,
            height: 4,
            borderRadius: '50%',
            backgroundColor:
              websocketStatus === 'connected'
                ? '#00ff00'
                : websocketStatus === 'connecting'
                  ? '#FF9800'
                  : '#ff4444',
            boxShadow:
              websocketStatus === 'connected'
                ? '0 0 4px rgba(0, 255, 0, 0.8)'
                : websocketStatus === 'connecting'
                  ? '0 0 4px rgba(255, 152, 0, 0.8)'
                  : '0 0 4px rgba(255, 68, 68, 0.8)',
            animation: websocketStatus === 'connecting' ? 'bnPulse 1.5s infinite' : 'none',
            '@keyframes bnPulse': {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.3 },
              '100%': { opacity: 1 },
            },
            zIndex: 3,
            pointerEvents: 'none',
          }}
          title={
            websocketStatus === 'connected'
              ? 'WebSocket conectado'
              : websocketStatus === 'connecting'
                ? 'WebSocket conectando...'
                : 'WebSocket desconectado'
          }
        />
      </Box>
    </>
  );
};

export default BottomNavBar;
